import NumericTextField from '@/components/form/numeric-text-field';
import { PageWrapper } from '@/components/page-wrapper/page-wrapper';
import { formatRm } from '@/components/table/table-format-helpers';
import { useErrorNotification } from '@/hooks/use-error-notification';
import { formatDateDisplay } from '@/utils/date-utils';
import { normalizeApiError } from '@/utils/error-utils';
import { Label, Table } from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import { useEffect, useState } from 'react';
import { usePriceForProduct } from './price-lookup-queries';

const parseOrNull = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const HEADERS = [
  'Product',
  'Price Unit',
  'Currency',
  'Status',
  'Valid From',
  'Valid To',
  'RRP',
  'LP',
  'RM%',
];

export const PriceLookupPage = () => {
  const [query, setQuery] = useState<{
    productId: number | null;
    priceUnitId: number | null;
  }>({ productId: null, priceUnitId: null });

  const { data, isFetching, isError, error } = usePriceForProduct(
    query.productId,
    query.priceUnitId,
  );

  const notifyError = useErrorNotification();
  useEffect(() => {
    if (isError && error) {
      notifyError(normalizeApiError(error), 'load prices');
    }
  }, [isError, error, notifyError]);

  const hasSearched = query.productId != null && query.priceUnitId != null;
  const rows = data ?? [];

  return (
    <PageWrapper headerProps={{ title: 'Find price' }} layout="scroll">
      <Flex flexDirection="column" gap={6} paddingTop={6} paddingBottom={6}>
        <Flex flexDirection="row" gap={4} alignItems="flex-end" flexWrap="wrap">
          <Flex flexDirection="column" gap={1} width="160px">
            <div>
              <Label>Product ID</Label>
              <NumericTextField
                integerOnly
                value={query.productId?.toString() ?? ''}
                onChange={(_e, value) =>
                  setQuery((prev) => ({
                    ...prev,
                    productId: parseOrNull(value),
                  }))
                }
                placeholder="Product ID"
              />
            </div>
          </Flex>
          <Flex flexDirection="column" gap={1} width="160px">
            <Text fontSize="sm">Price Unit ID</Text>
            <NumericTextField
              integerOnly
              value={query.priceUnitId?.toString() ?? ''}
              onChange={(_e, value) =>
                setQuery((prev) => ({
                  ...prev,
                  priceUnitId: parseOrNull(value),
                }))
              }
              placeholder="Price Unit ID"
            />
          </Flex>
        </Flex>

        {hasSearched && !isFetching && rows.length === 0 && (
          <Text>No prices found for the given Product and Price Unit.</Text>
        )}

        {rows.length > 0 && (
          <Table.Container>
            <Table aria-label="Price lookup results">
              <Table.Header>
                <Table.Row>
                  {HEADERS.map((header) => (
                    <Table.HeaderCell key={header}>{header}</Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((row) => (
                  <Table.Row
                    key={`${row.productId}-${row.priceUnitId}-${row.validFrom}-${row.priceStatus}`}
                  >
                    <Table.Cell>{row.productName}</Table.Cell>
                    <Table.Cell>{row.priceUnitDescription}</Table.Cell>
                    <Table.Cell>{row.currency}</Table.Cell>
                    <Table.Cell>{row.priceStatus}</Table.Cell>
                    <Table.Cell>{formatDateDisplay(row.validFrom)}</Table.Cell>
                    <Table.Cell>{formatDateDisplay(row.validTo)}</Table.Cell>
                    <Table.Cell>{row.rrp}</Table.Cell>
                    <Table.Cell>{row.lp}</Table.Cell>
                    <Table.Cell>{formatRm(row.rm)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Table.Container>
        )}
      </Flex>
    </PageWrapper>
  );
};
